import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  assertSuccessfulWorkerResponse,
  extractStatus,
  normalizeWorkerResponse,
} from "./helpers";
import { showSuccessToast } from "../../utils/showToastAlert";

export function usePostParticipantConnectorData({
  callApiWithWorker,
  t,
  onErrorAlert,
}) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["connectorData", "participants", "create"],
    mutationFn: async ({
      participantId,
      payload,
      method = "PUT",
      activateAfterSave = false,
    }) => {
      if (!participantId) throw new Error("Missing participantId");

      const normalizedMethod = String(method ?? "PUT")
        .trim()
        .toUpperCase();
      const res = await callApiWithWorker(
        `/registry/connector-data/${encodeURIComponent(participantId)}`,
        {
          method: normalizedMethod,
          connector: "springConnector",
          body: payload,
        },
      );

      const connectorData = assertSuccessfulWorkerResponse(
        res,
        "Create participant connector data failed",
      );

      if (!activateAfterSave) {
        return {
          connectorData,
        };
      }

      const activationRes = await callApiWithWorker(
        `/registry/participants/${encodeURIComponent(participantId)}/active`,
        {
          method: "POST",
          connector: "springConnector",
        },
      );

      const activationStatus = extractStatus(activationRes);
      const activationData = normalizeWorkerResponse(activationRes);

      if (activationStatus && activationStatus >= 400) {
        const e = new Error(
          `Activate participant failed (${activationStatus})`,
        );
        e.statusCode = activationStatus;
        e.response = activationData;
        throw e;
      }

      return {
        connectorData,
        activation: activationData,
      };
    },
    onSuccess: async (_data, vars) => {
      await qc.invalidateQueries({ queryKey: ["participants"] });
      await qc.invalidateQueries({
        queryKey: ["participants", "byId", vars?.participantId],
      });
      await qc.invalidateQueries({ queryKey: ["connectorData"] });

      showSuccessToast(
        t?.(
          "participant.technicalDataDialog.updated",
          "Technical data created successfully",
        ),
        {
          title: t?.("dataModels.createdMessage", "Created successfully"),
        },
      );
    },
    onError: async (err) => {
      if (onErrorAlert) return onErrorAlert(err);
    },
  });
}
